import type { AxiosError } from 'axios';
import axios from 'axios';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { UserOrganization, UserRole } from '../../../enum';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { fetchClinicianSites } from '../admin-clinicians/adminClinicianSlice';
import type { CheckIn, TotalClientDetail } from '../program-dashboard/modals/modalDashboardSlice';
import { dateTimeFilterFormat } from '../program-dashboard/program-dashboard.helper';
import type {
  DashboardRequestParams,
  Intervention,
  PopulationData,
  TimeInStatus,
} from '../program-dashboard/programDashboardSlice';
import type { SitesPopularity } from './program-dashboard.helper';

const { logError } = RaygunErrorHandlerService();

export type IncompleteSubmissionsData = {
  submissions_missing_both: number | null;
  submissions_with_missing_questions: number | null;
  submissions_without_pharmasmart_data: number | null;
};

type ProgramDashboardDataOLH = {
  previousParams: Partial<DashboardRequestParams>;
  issues: {
    number_of_clients_with_no_activity: number | null;
    incomplete_submissions: IncompleteSubmissionsData | null;
  };
  check_in_distribution_by_dow_hour: [number, number, number][];
  site_distribution: SitesPopularity[];
  client_activity_trends: {
    interventions: Intervention[];
    status_distribution: TimeInStatus[];
  };
};

type ProgramDashboardOLH = {
  programDashboardParams: DashboardRequestParams;
  programDashboardData: ProgramDashboardDataOLH;
  loadingDashboard: boolean;
  populationData: PopulationData;
  loadingPopulation: boolean;
  check_ins: CheckIn[] | null;
  loadingCheckIn: boolean;
  total_clients_details: TotalClientDetail[] | null;
  loadingTotalClient: boolean;
};

const initialState: ProgramDashboardOLH = {
  programDashboardParams: {
    app_code: 'olh',
    start_date: dayjs().subtract(1, 'month'),
    end_date: dayjs(),
    organization_id: UserOrganization.OLH,
  },
  programDashboardData: {
    previousParams: {},
    issues: {
      number_of_clients_with_no_activity: null,
      incomplete_submissions: null,
    },
    check_in_distribution_by_dow_hour: [],
    site_distribution: [],
    client_activity_trends: {
      interventions: [],
      status_distribution: [],
    },
  },
  populationData: {
    new_clients: null,
    clients_who_did_not_onboard: null,
    product_distribution: [],
    gender_distribution: [],
    caf_rcmp_distribution: [],
    age_distribution: [],
    primary_diagnosis_distribution: [],
  },
  loadingDashboard: false,
  loadingPopulation: false,
  loadingCheckIn: false,
  loadingTotalClient: false,
  check_ins: null,
  total_clients_details: [],
};

export const loadDashboard = createAsyncThunk<ProgramDashboardDataOLH, undefined, AsyncThunkConfig>(
  'programDashboardOLH/loadDashboard',
  async (_, thunkAPI) => {
    try {
      const currentUser = thunkAPI.getState().authSlice.currentUser;

      if (currentUser.role === UserRole.SITE && currentUser.user_id) {
        await thunkAPI.dispatch(fetchClinicianSites(currentUser.user_id));
        const site_id = thunkAPI.getState().adminClinicianSlice.clinicianSites[0]?.site_id;
        if (site_id) {
          thunkAPI.dispatch(handleParamsChange({ site_id }));
        }
      }

      const programDashboardParams = thunkAPI.getState().programDashboardOLHSlice.programDashboardParams;
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };

      const response = await axios.post('v3_program_dashboard', body, { signal: thunkAPI.signal });
      return (response as unknown as ProgramDashboardDataOLH) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadDashboard']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const loadPopulationDetails = createAsyncThunk<PopulationData, undefined, AsyncThunkConfig>(
  'programDashboardOLH/loadPopulationDetails',
  async (_, thunkAPI) => {
    try {
      const programDashboardParams = thunkAPI.getState().programDashboardOLHSlice.programDashboardParams;
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };

      const response = await axios.post('v3_population_details', body, { signal: thunkAPI.signal });
      return (response as unknown as PopulationData) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadPopulationDetails']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const loadTotalClientDetails = createAsyncThunk<TotalClientDetail[], undefined, AsyncThunkConfig>(
  'programDashboardOLH/loadTotalClientDetails',
  async (_, thunkAPI) => {
    try {
      const programDashboardParams = thunkAPI.getState().programDashboardOLHSlice.programDashboardParams;
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };

      const response = await axios.post('v3_all_client_data', body, { signal: thunkAPI.signal });
      return (response as unknown as TotalClientDetail[]) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadTotalClientDetails']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const loadCheckInDetails = createAsyncThunk<CheckIn[], undefined, AsyncThunkConfig>(
  'programDashboardOLH/loadCheckInDetails',
  async (_, thunkAPI) => {
    try {
      const programDashboardParams = thunkAPI.getState().programDashboardOLHSlice.programDashboardParams;
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };

      const response = await axios.post('v3_check_in_data', body, { signal: thunkAPI.signal });
      return (response as unknown as CheckIn[]) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadCheckInDetails']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const programDashboardOLHSlice = createSlice({
  name: 'programDashboardOLH',
  initialState,
  reducers: {
    handleParamsChange: (state, action: PayloadAction<Partial<DashboardRequestParams>>) => {
      state.programDashboardParams = { ...state.programDashboardParams, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDashboard.pending, (state, _action) => {
        state.loadingDashboard = true;
      })
      .addCase(loadDashboard.fulfilled, (state, action) => {
        state.programDashboardData = action.payload;
        state.programDashboardData.previousParams = {
          site_id: state.programDashboardParams.site_id,
          start_date: state.programDashboardParams.start_date,
          end_date: state.programDashboardParams.end_date,
          organization_id: state.programDashboardParams.organization_id,
        };
        state.loadingDashboard = false;
      })
      .addCase(loadDashboard.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingDashboard = false;
        }
      })
      .addCase(loadPopulationDetails.pending, (state, _action) => {
        state.loadingPopulation = true;
      })
      .addCase(loadPopulationDetails.fulfilled, (state, action) => {
        state.populationData = action.payload;
        state.loadingPopulation = false;
      })
      .addCase(loadPopulationDetails.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingPopulation = false;
        }
      })
      .addCase(loadTotalClientDetails.pending, (state, _action) => {
        state.loadingTotalClient = true;
      })
      .addCase(loadTotalClientDetails.fulfilled, (state, action) => {
        state.total_clients_details = action.payload;
        state.loadingTotalClient = false;
      })
      .addCase(loadTotalClientDetails.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingTotalClient = false;
        }
      })
      .addCase(loadCheckInDetails.pending, (state, _action) => {
        state.loadingCheckIn = true;
      })
      .addCase(loadCheckInDetails.fulfilled, (state, action) => {
        state.check_ins = action.payload;
        state.loadingCheckIn = false;
      })
      .addCase(loadCheckInDetails.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingCheckIn = false;
        }
      });
  },
});

export const { handleParamsChange } = programDashboardOLHSlice.actions;
